import React, { useEffect } from 'react';

interface SEOProps {
    title: string;
    description: string;
    keywords?: string;
    canonical?: string;
    type?: 'website' | 'article' | 'product';
    publishedTime?: string;
    modifiedTime?: string;
    authorName?: string;
    category?: string;
    faqs?: Array<{ question: string; answer: string }>;
    breadcrumbs?: Array<{ name: string; url: string }>;
}

const SEO: React.FC<SEOProps> = ({ 
    title, 
    description, 
    keywords, 
    canonical, 
    type = 'website',
    publishedTime = '2026-09-01T08:00:00+01:00',
    modifiedTime = '2026-10-22T08:00:00+01:00',
    authorName = 'Bytecohort Engineering Team',
    category = 'WhatsApp Automation Nigeria',
    faqs,
    breadcrumbs
}) => {
    const siteTitle = "Bytecohort Reach";
    const fullTitle = title.includes(siteTitle) ? title : `${title} | ${siteTitle}`;

    const baseKeywords = "WhatsApp Automation Nigeria, WhatsApp Marketing Nigeria, WhatsApp Chatbot Nigeria, Bulk WhatsApp Sender Nigeria, Official Meta Cloud API Nigeria, Google Sheets WhatsApp Sync, Bytecohort Reach, Gemini AI Agent";
    const finalKeywords = keywords ? `${keywords}, ${baseKeywords}` : baseKeywords;

    const resolveCanonical = (url?: string) => {
        if (!url) {
            const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';
            const cleanPath = currentPath === '/' || currentPath === '/index.html' ? '/' : currentPath.replace(/\/$/, '');
            return `https://bytecohortreach.com${cleanPath}`;
        }
        let normalized = url.trim();
        normalized = normalized.replace(/^https?:\/\/(?:reach\.bytecohort\.com|www\.bytecohortreach\.com)/, 'https://bytecohortreach.com');
        if (normalized.startsWith('/')) {
            normalized = `https://bytecohortreach.com${normalized}`;
        }
        return normalized;
    };
    const validCanonical = resolveCanonical(canonical);
    const ogImageUrl = "https://bytecohortreach.com/email_header_banner.png";

    useEffect(() => {
        document.title = fullTitle;

        const updateMeta = (name: string, content: string, attribute: string = 'name') => {
            let element = document.querySelector(`meta[${attribute}="${name}"]`);
            if (!element) {
                element = document.createElement('meta');
                element.setAttribute(attribute, name);
                document.head.appendChild(element);
            }
            element.setAttribute('content', content);
        };

        const updateLink = (rel: string, href: string) => {
            let element = document.querySelector(`link[rel="${rel}"]`);
            if (!element) {
                element = document.createElement('link');
                element.setAttribute('rel', rel);
                document.head.appendChild(element);
            }
            element.setAttribute('href', href);
        };

        // Standard Meta Tags
        updateMeta('description', description);
        updateMeta('keywords', finalKeywords);
        updateMeta('geo.region', 'NG');
        updateMeta('geo.placename', 'Lagos, Nigeria');

        // Open Graph / Facebook
        updateMeta('og:type', type, 'property');
        updateMeta('og:title', fullTitle, 'property');
        updateMeta('og:description', description, 'property');
        updateMeta('og:url', validCanonical, 'property');
        updateMeta('og:site_name', siteTitle, 'property');
        updateMeta('og:locale', 'en_NG', 'property');
        updateMeta('og:image', ogImageUrl, 'property');

        if (type === 'article') {
            updateMeta('article:published_time', publishedTime, 'property');
            updateMeta('article:modified_time', modifiedTime, 'property');
            updateMeta('article:section', category, 'property');
            updateMeta('article:author', authorName, 'property');
        }

        // Twitter
        updateMeta('twitter:card', 'summary_large_image');
        updateMeta('twitter:title', fullTitle);
        updateMeta('twitter:description', description);
        updateMeta('twitter:image', ogImageUrl);

        // Canonical Link
        updateLink('canonical', validCanonical);

        // Dynamic JSON-LD structured data injection
        let schemaElement = document.querySelector('script#dynamic-seo-schema[type="application/ld+json"]') || document.querySelector('script[type="application/ld+json"]');
        if (!schemaElement) {
            schemaElement = document.createElement('script');
            schemaElement.setAttribute('id', 'dynamic-seo-schema');
            schemaElement.setAttribute('type', 'application/ld+json');
            document.head.appendChild(schemaElement);
        }

        const defaultBreadcrumbs = breadcrumbs || (
            validCanonical.includes('/blog/')
                ? [
                    { name: 'Home', url: 'https://bytecohortreach.com/' },
                    { name: 'WhatsApp Automation Nigeria', url: 'https://bytecohortreach.com/whatsapp-automation-nigeria' },
                    { name: 'Engineering Playbooks', url: 'https://bytecohortreach.com/blog' },
                    { name: title, url: validCanonical }
                ]
                : [
                    { name: 'Home', url: 'https://bytecohortreach.com/' },
                    { name: 'WhatsApp Automation Nigeria', url: 'https://bytecohortreach.com/whatsapp-automation-nigeria' },
                    { name: 'Pricing', url: 'https://bytecohortreach.com/pricing' },
                    { name: 'Playbooks & Blog', url: 'https://bytecohortreach.com/blog' }
                ]
        );

        const graphNodes: any[] = [
            {
                "@type": "WebSite",
                "@id": "https://bytecohortreach.com/#website",
                "url": "https://bytecohortreach.com/",
                "name": "Bytecohort Reach",
                "inLanguage": "en-NG",
                "description": "The #1 WhatsApp Automation, AI Chatbot Builder & Google Sheets Sync Platform in Nigeria.",
                "publisher": {
                    "@id": "https://bytecohortreach.com/#organization"
                },
                "potentialAction": {
                    "@type": "SearchAction",
                    "target": "https://bytecohortreach.com/blog?q={search_term_string}",
                    "query-input": "required name=search_term_string"
                }
            },
            {
                "@type": "Organization",
                "@id": "https://bytecohortreach.com/#organization",
                "name": "Bytecohort Reach",
                "url": "https://bytecohortreach.com/",
                "logo": {
                    "@type": "ImageObject",
                    "url": "https://bytecohortreach.com/logo.png"
                },
                "areaServed": [
                    { "@type": "Country", "name": "Nigeria" },
                    { "@type": "AdministrativeArea", "name": "Lagos" },
                    { "@type": "AdministrativeArea", "name": "Abuja" },
                    { "@type": "AdministrativeArea", "name": "Port Harcourt" }
                ],
                "sameAs": [
                    "https://twitter.com/bytecohort",
                    "https://github.com/bytecohort"
                ]
            },
            {
                "@type": "BreadcrumbList",
                "@id": `${validCanonical}#breadcrumb`,
                "itemListElement": defaultBreadcrumbs.map((b, idx) => ({
                    "@type": "ListItem",
                    "position": idx + 1,
                    "name": b.name,
                    "item": b.url
                }))
            }
        ];

        if (type === 'article') {
            graphNodes.push({
                "@type": ["BlogPosting", "TechArticle"],
                "@id": `${validCanonical}#article`,
                "mainEntityOfPage": {
                    "@type": "WebPage",
                    "@id": validCanonical
                },
                "headline": title.slice(0, 110),
                "description": description,
                "image": ogImageUrl,
                "datePublished": publishedTime,
                "dateModified": modifiedTime,
                "inLanguage": "en-NG",
                "articleSection": category,
                "keywords": finalKeywords,
                "author": {
                    "@type": "Organization",
                    "name": authorName,
                    "url": "https://bytecohortreach.com/"
                },
                "publisher": {
                    "@id": "https://bytecohortreach.com/#organization"
                },
                "about": {
                    "@type": "Thing",
                    "name": "WhatsApp Automation in Nigeria"
                }
            });
        } else {
            graphNodes.push({
                "@type": "SoftwareApplication",
                "@id": "https://bytecohortreach.com/#software",
                "name": "Bytecohort Reach",
                "description": description,
                "applicationCategory": "BusinessApplication",
                "operatingSystem": "Web, Cloud, Android, iOS",
                "keywords": finalKeywords,
                "areaServed": {
                    "@type": "Country",
                    "name": "Nigeria"
                },
                "aggregateRating": {
                    "@type": "AggregateRating",
                    "ratingValue": "4.9",
                    "ratingCount": "240",
                    "bestRating": "5",
                    "worstRating": "1"
                },
                "offers": [
                    {
                        "@type": "Offer",
                        "name": "Starter Plan (Nigeria)",
                        "price": "29000.00",
                        "priceCurrency": "NGN",
                        "description": "Official Meta WhatsApp Cloud API bulk broadcasts & Naira billing via Paystack.",
                        "eligibleRegion": { "@type": "Country", "name": "Nigeria" }
                    },
                    {
                        "@type": "Offer",
                        "name": "Pro Plan (Nigeria)",
                        "price": "75000.00",
                        "priceCurrency": "NGN",
                        "description": "Two-way Google Sheets sync, visual Gemini AI Agent Builder, and team inbox.",
                        "eligibleRegion": { "@type": "Country", "name": "Nigeria" }
                    }
                ]
            });
        }

        const faqItems = faqs && faqs.length > 0 ? faqs : [
            {
                question: "What is the best WhatsApp automation platform in Nigeria?",
                answer: "Bytecohort Reach is the #1 official Meta WhatsApp Cloud API and AI automation platform in Nigeria. It offers 100% anti-ban compliance, two-way Google Sheets synchronization, visual Gemini AI chatbots, and local Naira (₦) billing via Paystack."
            },
            {
                question: "Can I pay for WhatsApp API and automation in Nigerian Naira (₦)?",
                answer: "Yes. Bytecohort Reach supports native Naira (₦) payments via Paystack, Verve, Mastercard, Visa, and bank transfer without requiring a USD virtual card."
            },
            {
                question: "Will my Nigerian +234 WhatsApp business number get banned?",
                answer: "No. Unlike unofficial browser clickers such as Wasender or GBWhatsApp, Bytecohort Reach uses the official Meta WhatsApp Cloud API with verified templates, protecting your +234 phone number from bans."
            }
        ];

        graphNodes.push({
            "@type": "FAQPage",
            "@id": `${validCanonical}#faq`,
            "mainEntity": faqItems.map(f => ({
                "@type": "Question",
                "name": f.question,
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": f.answer
                }
            }))
        });

        schemaElement.textContent = JSON.stringify({
            "@context": "https://schema.org",
            "@graph": graphNodes
        }, null, 2);

    }, [fullTitle, description, finalKeywords, validCanonical, type, siteTitle, faqs, publishedTime, modifiedTime, authorName, category, breadcrumbs, title]);

    return null;
};

export default SEO;